/**
 * KAN-910: одна точка чтения и записи параметров адреса для фильтров CRM
 * (таблица, доска, "Лиды по дням"): ссылку можно отправить, получатель увидит то же.
 *
 * - update: запись с replace (история не засоряется); push только по явной просьбе
 *   (открытие карточки, "Назад" закрывает).
 * - Несколько записей в одном такте складываются: setSearchParams из react-router
 *   отдаёт функции-обновлению параметры своего рендера, и вторая запись в том же
 *   такте (умолчания таблицы и вида в одном коммите) затирала бы первую. Поэтому
 *   основа следующей записи - результат предыдущей, пока рендер его не догнал.
 * - useUrlDefaults: правило источника. Параметр в адресе главнее; нет его - умолчание
 *   (предпочтение из localStorage или дефолт), и оно сразу пишется в адрес, чтобы
 *   скопированная ссылка была полной.
 * - useUrlFlag: булев флаг '1'/'0' в адресе с запасным предпочтением в localStorage.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { readStoredFlag, writeStoredFlag } from './useStoredFlag';

/** Изменение: правит копию на месте или возвращает новые параметры. */
export type UrlChange = (next: URLSearchParams) => URLSearchParams | void;

export interface UrlWriteOptions {
  /** Новая запись истории (по умолчанию replace). */
  push?: boolean;
}

/** Записи такта: from - адрес до первой из них (его ещё видит рендер), to - после последней. */
let pending: { from: string; to: string } | null = null;

function normalizeSearch(search: string): string {
  return search.startsWith('?') ? search.slice(1) : search;
}

function baseFor(search: string): URLSearchParams {
  const current = normalizeSearch(search);
  if (pending && (pending.from === current || pending.to === current)) return new URLSearchParams(pending.to);
  return new URLSearchParams(current);
}

export interface UrlState {
  params: URLSearchParams;
  update: (change: UrlChange, options?: UrlWriteOptions) => void;
  setParam: (name: string, value: string | null, options?: UrlWriteOptions) => void;
}

export function useUrlState(): UrlState {
  const location = useLocation();
  const navigate = useNavigate();
  const { search } = location;
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const update = useCallback(
    (change: UrlChange, options: UrlWriteOptions = {}) => {
      const base = baseFor(search);
      const from = base.toString();
      const draft = new URLSearchParams(base);
      const next = change(draft) ?? draft;
      const to = next.toString();
      if (to === from) return;
      // Цепочка записей одного такта: исходный адрес такта сохраняется, иначе третья
      // запись (рендер ещё на исходном адресе) не узнала бы pending и затёрла первые две.
      const chained = pending !== null && pending.to === from;
      pending = { from: chained && pending ? pending.from : from, to };
      queueMicrotask(() => {
        if (pending && pending.to === to) pending = null;
      });
      navigate({ search: to ? `?${to}` : '' }, { replace: !options.push });
    },
    [navigate, search]
  );

  const setParam = useCallback(
    (name: string, value: string | null, options?: UrlWriteOptions) =>
      update((next) => {
        if (value === null || value === '') next.delete(name);
        else next.set(name, value);
      }, options),
    [update]
  );

  return { params, update, setParam };
}

/** Умолчание параметра: пишется, если в адресе нет key и ни одного из unless. */
export interface UrlDefault {
  key: string;
  /** null - умолчание пока неизвестно, не пишется. */
  value: string | null;
  /** Параметры, при которых умолчание не нужно (например, даты вместо имени периода). */
  unless?: readonly string[];
}

/** Недостающие умолчания одной записью с replace. enabled false - ничего не пишет. */
export function useUrlDefaults(defaults: readonly UrlDefault[], enabled = true): void {
  const { params, update } = useUrlState();
  const missing = defaults.filter(
    (d) => d.value !== null && !params.has(d.key) && !(d.unless ?? []).some((key) => params.has(key))
  );
  const signature = enabled ? missing.map((d) => `${d.key}=${d.value}`).join('&') : '';

  useEffect(() => {
    if (!signature) return;
    update((next) => {
      new URLSearchParams(signature).forEach((value, key) => {
        if (!next.has(key)) next.set(key, value);
      });
    });
  }, [signature, update]);
}

const FLAG_ON = '1';
const FLAG_OFF = '0';

/**
 * Булев флаг в адресе (?key=1|0). Нет параметра - предпочтение из localStorage
 * (storageKey) или fallback, и оно пишется в адрес. Переключение пишет адрес и
 * запоминает выбор как предпочтение для следующих открытий без параметра.
 */
export function useUrlFlag(key: string, storageKey: string, fallback: boolean): [boolean, () => void] {
  const { params, setParam } = useUrlState();
  const raw = params.get(key);
  const value = raw === FLAG_ON ? true : raw === FLAG_OFF ? false : readStoredFlag(storageKey, fallback);
  useUrlDefaults([{ key, value: value ? FLAG_ON : FLAG_OFF }], raw !== FLAG_ON && raw !== FLAG_OFF);

  const toggle = useCallback(() => {
    const next = !value;
    writeStoredFlag(storageKey, next);
    setParam(key, next ? FLAG_ON : FLAG_OFF);
  }, [key, setParam, storageKey, value]);

  return [value, toggle];
}
